import { Component, Output, EventEmitter, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../global/services/global.service';
import { WorkspaceService } from '../../workspace/workspace.service';
import { AppService } from '../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
  hydratedGlobalParams,
} from '../../shared/workspace-element-context';
import { EcRefreshSetupService } from './ec-refresh-setup.service';
import { GtwSnackbarService } from '../../shared/_services/gtw-snackbar.service';
import { GtwElementsBaseUrlsModel } from '../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';
import moment from 'moment';

@Component({
    selector: 'gtw-ec-refresh-setup-ce',
    templateUrl: './ec-refresh-setup.component.html',
    styleUrls: ['./ec-refresh-setup.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class EcRefreshSetupComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  private readonly subs = new Subscription();

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};
  headerData: any = {};

  // Global service references
  globalService: any;
  USER_SETTINGS: any;
  SERVER_CONFIG: any;
  GENERAL_CONFIG: any;

  // Make Object available in template
  Object = Object;

  refreshForm: UntypedFormGroup;
  isSaving = false;
  isSavingUpdate = false;
  isSavingDelete = false;
  isLoading = false;
  minimise = false;
  modalTitle = "EC Refresh Setup";
  crudLoading = false;
  isCreateMode = true;

  // Data arrays
  allScenarioTypeCode: any[] = [];
  allScenarioDesc: any[] = [];
  allGroupObjByYear: any[] = [];
  errorMessage: string = '';

  // Date picker options for year-only selection
  minYear: string = '';
  maxYear: string = '';

  constructor(
    private fb: UntypedFormBuilder,
    private ecRefreshSetupService: EcRefreshSetupService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {
    this.refreshForm = this.fb.group({
      TAX_YEAR: ['', Validators.required],
      SCENARIO_TYPE_CODE: ['', Validators.required],
      SCENARIO_DESC: ['', Validators.required],
      GROUP_OBJ_KEY: ['', Validators.required],
      BATCH_START_DATE: ['', Validators.required],
      BATCH_END_DATE: [''],
      DEFAULT_TAX_YEAR_BEGIN: ['', Validators.required],
      DEFAULT_TAX_YEAR_END: ['', Validators.required],
      REFRESH_STATUS: ['0'],
      DEFAULT_FILING_CYCLE: ['', Validators.required]
    });
  }

  ngOnInit(): void {
    this.initializeYearConstraints();
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.init();
    }));
    this.init();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    header.globalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || header.globalParams || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.globalService = header;
    this.headerData = header;
    this.USER_SETTINGS = header.userSettingsData || {};
    this.SERVER_CONFIG = header.serverConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    this.rowData = rowPayloadFromActiveScreen(activeScreen);
    this.cdr.markForCheck();
  }

  init(): void {
    console.log('EcRefreshSetupComponent init() called');
    console.log('baseURLs:', this.baseURLs);
    console.log('filterParams:', this.filterParams);
    console.log('rowData:', this.rowData);
    console.log('GENERAL_CONFIG:', this.GENERAL_CONFIG);
    
    // Check if we have rowData for update mode
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      this.isCreateMode = false;
      this.modalTitle = "Refresh Setup";
    } else {
      this.isCreateMode = true;
      this.modalTitle = "EC Refresh Setup";
    }
    
    // Only proceed if we have all required data
    if (this.GENERAL_CONFIG && 
        this.GENERAL_CONFIG.base_url &&
        this.filterParams && 
        Object.keys(this.filterParams).length > 0) {
      console.log('All required data available, calling initializeForm() and loadInitialData()');
      this.initializeForm();
      this.loadInitialData();
    } else {
      console.log('Missing required data, not calling loadInitialData()');
      console.log('GENERAL_CONFIG check:', !!this.GENERAL_CONFIG);
      console.log('base_url check:', !!this.GENERAL_CONFIG?.base_url);
      console.log('filterParams check:', !!this.filterParams);
      console.log('filterParams length:', this.filterParams ? Object.keys(this.filterParams).length : 0);
      
      // 🚀 CRITICAL: Show content even without data to prevent blank modal
      this.isLoading = false;
      this.gtwSnackbarService.openSnackBar('Component loaded with limited functionality - some features may not work', 'gtw-snackbar--yellow');
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private initializeForm(): void {
    if (this.rowData && Object.keys(this.rowData).length > 0) {
      // Update mode - populate form with existing data
      this.isCreateMode = false;
      this.modalTitle = "Refresh Setup";
      this.applyScenarioValidators(false);
      this.refreshForm.patchValue({
        TAX_YEAR: this.rowData.TAX_YEAR || '',
        SCENARIO_TYPE_CODE: this.rowData.SCENARIO_TYPE_CODE || '',
        SCENARIO_DESC: this.rowData.SCENARIO_DESC || '',
        GROUP_OBJ_KEY: this.rowData.GROUP_OBJ_KEY || '',
        BATCH_START_DATE: this.convertDateToYearFormat(this.rowData.BATCH_START_DATE),
        BATCH_END_DATE: this.convertDateToYearFormat(this.rowData.BATCH_END_DATE),
        DEFAULT_TAX_YEAR_BEGIN: this.convertDateToYearFormat(this.rowData.DEFAULT_TAX_YEAR_BEGIN),
        DEFAULT_TAX_YEAR_END: this.convertDateToYearFormat(this.rowData.DEFAULT_TAX_YEAR_END),
        REFRESH_STATUS: this.rowData.REFRESH_STATUS || '0',
        DEFAULT_FILING_CYCLE: this.rowData.DEFAULT_FILING_CYCLE || ''
      });
    } else {
      // Create mode - set default values
      this.isCreateMode = true;
      this.modalTitle = "EC Refresh Setup";
      this.applyScenarioValidators(true);
      const taxYear = this.filterParams?.tax_year || new Date().getFullYear();
      this.refreshForm.patchValue({
        TAX_YEAR: taxYear,
        SCENARIO_TYPE_CODE: '',
        SCENARIO_DESC: '',
        GROUP_OBJ_KEY: '',
        BATCH_START_DATE: '',
        BATCH_END_DATE: '',
        DEFAULT_TAX_YEAR_BEGIN: this.convertDateToYearFormat(`01/01/${taxYear}`),
        DEFAULT_TAX_YEAR_END: this.convertDateToYearFormat(`12/31/${taxYear}`),
        REFRESH_STATUS: '0',
        DEFAULT_FILING_CYCLE: ''
      });
    }
  }

  // Legacy update screen has no Scenario Type Code / Scenario Desc fields,
  // so those controls must not block the Update action.
  private applyScenarioValidators(required: boolean): void {
    ['SCENARIO_TYPE_CODE', 'SCENARIO_DESC'].forEach(name => {
      const control = this.refreshForm.get(name);
      if (!control) {
        return;
      }
      if (required) {
        control.setValidators([Validators.required]);
      } else {
        control.clearValidators();
      }
      control.updateValueAndValidity({ emitEvent: false });
    });
  }

  private loadInitialData(): void {
    this.isLoading = true;
    this.loadScenarioTypeCode();
  }

  loadScenarioTypeCode(): void {
    this.ecRefreshSetupService.loadScenarioTypeCode(this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.allScenarioTypeCode = data || [];
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading scenario type codes:', error);
          this.gtwSnackbarService.openSnackBar('Failed to load scenario type codes', 'gtw-snackbar--red');
          this.isLoading = false;
          this.cdr.markForCheck();
        }
      });
  }

  onScenarioTypeCodeChange(): void {
    const selectedValue = this.refreshForm.get('SCENARIO_TYPE_CODE')?.value;
    if (selectedValue) {
      this.loadScenarioDesc(selectedValue);
      this.refreshForm.patchValue({ GROUP_OBJ_KEY: '' });
    }
  }

  loadScenarioDesc(selectedValue: string): void {
    this.ecRefreshSetupService.loadScenarioDesc(selectedValue, this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.allScenarioDesc = data || [];
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading scenario descriptions:', error);
          this.gtwSnackbarService.openSnackBar('Failed to load scenario descriptions', 'gtw-snackbar--red');
          this.cdr.markForCheck();
        }
      });
  }

  onScenarioDescChange(): void {
    const scenarioTypeCode = this.refreshForm.get('SCENARIO_TYPE_CODE')?.value;
    const scenarioDesc = this.refreshForm.get('SCENARIO_DESC')?.value;
    if (scenarioTypeCode && scenarioDesc) {
      this.loadGroupObjKey(scenarioTypeCode, scenarioDesc);
    }
  }

  loadGroupObjKey(scenarioTypeCode: string, scenarioDesc: string): void {
    this.ecRefreshSetupService.loadGroupObjKey(scenarioTypeCode, scenarioDesc, this.GENERAL_CONFIG.base_url, this.filterParams, this.rowData).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (data) => {
          this.allGroupObjByYear = data || [];
          this.cdr.markForCheck();
        },
        error: (error) => {
          console.error('Error loading group object keys:', error);
          this.gtwSnackbarService.openSnackBar('Failed to load group object keys', 'gtw-snackbar--red');
          this.cdr.markForCheck();
        }
      });
  }

  initializeYearConstraints(): void {
    const currentYear = new Date().getFullYear();
    const startYear = currentYear - 10; // 10 years back
    const endYear = currentYear + 10;   // 10 years forward
    
    // Set min and max year constraints for date inputs
    this.minYear = `${startYear}-01-01`;
    this.maxYear = `${endYear}-12-31`;
  }

  convertDateToYearFormat(date: any): string {
    if (!date) return '';
    
    // Already ISO - keep only the date part, native date inputs reject a time component
    if (typeof date === 'string' && /^\d{4}-\d{2}-\d{2}/.test(date)) {
      return date.substring(0, 10);
    }
    
    // If it's in MM/DD/YYYY format, convert to YYYY-MM-DD
    if (typeof date === 'string' && date.includes('/')) {
      const parts = date.split('/');
      if (parts.length === 3) {
        return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
    }
    
    // Convert year number to date string (YYYY-01-01 format)
    const yearNum = parseInt(date.toString());
    if (!isNaN(yearNum)) {
      return `${yearNum}-01-01`;
    }
    
    return '';
  }

  convertYearToDateString(date: any): string {
    if (!date) return '';
    
    // If it's a date string, extract the year and format as MM/DD/YYYY
    if (typeof date === 'string' && date.includes('-')) {
      return moment(date).format('MM/DD/YYYY');
    }
    
    // If it's already in MM/DD/YYYY format, return as is
    if (typeof date === 'string' && date.includes('/')) {
      return date;
    }
    
    // Convert year number to MM/DD/YYYY format
    const yearNum = parseInt(date.toString());
    if (!isNaN(yearNum)) {
      return moment(`${yearNum}-01-01`).format('MM/DD/YYYY');
    }
    
    return '';
  }

  onBatchStartDateChange(): void {
    this.validateDateRange();
  }

  onBatchEndDateChange(): void {
    const batchEndDate = this.refreshForm.get('BATCH_END_DATE')?.value;
    if (!batchEndDate) {
      this.refreshForm.patchValue({ REFRESH_STATUS: '0' });
    } else {
      this.refreshForm.patchValue({ REFRESH_STATUS: '40' });
      this.validateDateRange();
    }
  }

  validateDateRange(): void {
    if (!this.isBatchEndBeforeStart()) {
      return;
    }

    this.gtwSnackbarService.openSnackBar('Batch End Date cannot be earlier than Batch Start Date', 'gtw-snackbar--red');
    // Clear the end date to prevent invalid state
    this.refreshForm.patchValue({ BATCH_END_DATE: '', REFRESH_STATUS: '0' });
  }

  private isBatchEndBeforeStart(): boolean {
    const startDate = this.refreshForm.get('BATCH_START_DATE')?.value;
    const endDate = this.refreshForm.get('BATCH_END_DATE')?.value;

    if (!startDate || !endDate) {
      return false;
    }

    return moment(endDate, 'YYYY-MM-DD').isBefore(moment(startDate, 'YYYY-MM-DD'));
  }

  isValid(): boolean {
    // Check basic form validity
    if (!this.refreshForm.valid) {
      return false;
    }

    if (this.isBatchEndBeforeStart()) {
      this.gtwSnackbarService.openSnackBar('Batch End Date cannot be earlier than Batch Start Date', 'gtw-snackbar--red');
      return false;
    }

    return true;
  }

  // Controls hold ISO dates for the native date inputs; the API expects MM/DD/YYYY
  private buildPayload(): any {
    const payload = _.merge({}, this.refreshForm.value, this.filterParams);
    return this.ecRefreshSetupService.convertDateFormats(payload);
  }

  save(): void {
    if (!this.isValid()) {
      this.markFormGroupTouched();
      this.gtwSnackbarService.openSnackBar('Please fill all required fields and ensure dates are valid', 'gtw-snackbar--red');
      return;
    }

    this.isSaving = true;

    this.ecRefreshSetupService.save(this.buildPayload(), this.filterParams, this.rowData, this.GENERAL_CONFIG.base_url).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.isSaving = false;
          if (response.callSuccess === "1" && response.errorMessage === "null") {
            this.gtwSnackbarService.openSnackBar('EC Refresh Setup created successfully', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Save failed', 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.isSaving = false;
          this.gtwSnackbarService.openSnackBar('Save operation failed', 'gtw-snackbar--red');
          console.error('Save error:', error);
          this.cdr.markForCheck();
        }
      });
  }

  update(): void {
    if (!this.isValid()) {
      this.markFormGroupTouched();
      this.gtwSnackbarService.openSnackBar('Please fill all required fields and ensure dates are valid', 'gtw-snackbar--red');
      return;
    }

    this.isSavingUpdate = true;

    this.ecRefreshSetupService.update(this.buildPayload(), this.filterParams, this.rowData, this.GENERAL_CONFIG.base_url).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response: any) => {
          this.isSavingUpdate = false;
          if (response.callSuccess === "1" && response.errorMessage === "null") {
            this.gtwSnackbarService.openSnackBar('EC Refresh Setup updated successfully', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Update failed', 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        },
        error: (error: any) => {
          this.isSavingUpdate = false;
          this.gtwSnackbarService.openSnackBar('Update operation failed', 'gtw-snackbar--red');
          console.error('Update error:', error);
          this.cdr.markForCheck();
        }
      });
  }

  delete(): void {
    const taxYear = this.refreshForm.get('TAX_YEAR')?.value;
    const groupObjKey = this.refreshForm.get('GROUP_OBJ_KEY')?.value;

    if (!taxYear || !groupObjKey) {
      this.gtwSnackbarService.openSnackBar('Cannot delete: Missing required data', 'gtw-snackbar--red');
      return;
    }

    this.isSavingDelete = true;

    this.ecRefreshSetupService.delete(taxYear, groupObjKey, this.filterParams, this.rowData, this.GENERAL_CONFIG.base_url).pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.isSavingDelete = false;
          if (response.callSuccess === "1" && response.errorMessage === "null") {
            this.gtwSnackbarService.openSnackBar('EC Refresh Setup deleted successfully', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(response.errorMessage || 'Delete failed', 'gtw-snackbar--red');
          }
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.isSavingDelete = false;
          this.gtwSnackbarService.openSnackBar('Delete operation failed', 'gtw-snackbar--red');
          console.error('Delete error:', error);
          this.cdr.markForCheck();
        }
      });
  }

  reset(): void {
    this.refreshForm.reset();
    if (this.isCreateMode) {
      this.allScenarioDesc = [];
      this.allGroupObjByYear = [];
    }
    this.initializeForm();
    this.refreshForm.markAsUntouched();
    this.cdr.markForCheck();
  }

  cancel(): void {
    // AngularJS handles modal close automatically via event listener
    const updateEvent = new CustomEvent('request-grid-update', {
      detail: 'cancel',
      bubbles: true
    });
    
    if (document.querySelector('gtw-ec-refresh-setup-ce')) {
      document.querySelector('gtw-ec-refresh-setup-ce')?.dispatchEvent(updateEvent);
    }
  }


  private markFormGroupTouched(): void {
    Object.keys(this.refreshForm.controls).forEach(key => {
      const control = this.refreshForm.get(key);
      control?.markAsTouched();
    });
  }

  toggleMinimize(): void {
    this.minimise = !this.minimise;
  }
}

